import { Listbox, Transition } from '@headlessui/react';
import { ReactComponent as CheckedIcon } from 'components/icons/icon-checked.svg';
import { isEmpty } from 'lodash';
import querystring from 'querystring';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from 'store';
import {
  useGetStoreInfoQuery,
  useGetStoresQuery,
} from 'store/services/segmentsApi';
import { selectStore, userDetailsState } from 'store/userDetailsSlice';
import { useParseURLQueryParams } from 'utils';

export default function StoreSelector({
  fullSize,
  mobile,
}: {
  fullSize: boolean;
  mobile?: boolean;
}) {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { stores, currentStore, role_id } = useSelector(userDetailsState);
  const { query } = useParseURLQueryParams();

  const queryParams = `store_name=${query.store_name}`;

  const { data: storeInfo } = useGetStoreInfoQuery(queryParams, {
    skip: !!!query.store_name || role_id !== 1,
  });

  // Fetch Stores list/complete info
  useGetStoresQuery(undefined);

  const isTreslAdmin = role_id === 1;

  // Redirect if user has no store
  useEffect(() => {
    if (isTreslAdmin) return;

    const allowedPathnames = ['account', 'integrations/shopify'];

    const isAllowedPathname = allowedPathnames.some((substring) =>
      history.location.pathname.includes(substring)
    );

    if (isEmpty(stores) && !!!isAllowedPathname) {
      history.push('/account/stores/add');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role_id, stores, history.location.pathname]);

  // //TODO This Method needs revision when updating the URLQUeryparams
  useEffect(
    () => {
      // Add store name to URL if store is selected
      if (!!!query.store_name && currentStore) {
        const excludePaths = ['account', 'shopify', 'charge'];

        const isAccountSettingsPath = excludePaths.some((substring) =>
          history.location.pathname.includes(substring)
        );
        if (!!!isAccountSettingsPath) {
          history.push({
            search: querystring.stringify({
              store_name: currentStore.completeInfo?.store_name,
              ...query,
            }),
          });
        }

        return;
      }
    },
    // Only run on path change, any attempt to run in different situation might lead to UB
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history.location.pathname, history.location.search]
  );

  useEffect(() => {
    if (query.store_name !== currentStore.completeInfo?.store_name) {
      // Select store based on the URL
      const userStore = stores?.find(
        (store) =>
          store.store_name?.toLowerCase() === query.store_name?.toLowerCase()
      );

      if (userStore) {
        dispatch(selectStore({ storeId: Number(userStore.id) }));
        return;
      } else if (!!!userStore) {
        if (isTreslAdmin && storeInfo) {
          dispatch(selectStore({ store: storeInfo }));
        } else if (!!!isTreslAdmin) {
          history.push({
            search: '',
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStore.completeInfo?.store_name,
    isTreslAdmin,
    query.store_name,
    storeInfo,
    stores,
  ]);

  const handleOnChange = (selectedStoreId: number) => {
    dispatch(selectStore({ storeId: selectedStoreId }));
    const newStore = stores?.find((store) => store.id === selectedStoreId);
    history.push({
      search: querystring.stringify({
        ...query,
        store_name: newStore?.store_name,
      }),
    });
  };

  const subscriptionLevel = useMemo(() => {
    if (!!!currentStore) return 'Loading...';

    if (currentStore.subscriptionPlan?.type !== 'regular') {
      return (
        <div>
          {currentStore.completeInfo?.active_sub?.level}{' '}
          <span className="px-2 font-bold text-white rounded-full bg-segments-danger">
            Trial
          </span>
        </div>
      );
    } else {
      return currentStore.completeInfo?.active_sub?.level;
    }
  }, [currentStore]);

  return (
    <div className="flex items-center justify-center">
      <div className="w-full max-w-xs mx-auto">
        <Listbox
          as="div"
          className="space-y-1"
          value={currentStore.completeInfo?.id}
          onChange={handleOnChange}
        >
          {({ open }) => (
            <>
              <div className="relative">
                <span className="inline-block w-full rounded-md shadow-sm">
                  <Listbox.Button className="relative w-full py-2 text-left text-white transition duration-150 ease-in-out rounded-md cursor-pointer focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5">
                    {fullSize && (
                      <>
                        <span className="block pr-4 mb-1 text-2xl font-bold leading-7 truncate">
                          {currentStore.completeInfo?.name ||
                            currentStore.completeInfo?.store_name ||
                            'No Store'}
                        </span>
                        <span className="block leading-4 truncate text-segments-13px">
                          {subscriptionLevel}
                        </span>
                      </>
                    )}
                    {!mobile && (
                      <span className="absolute inset-y-0 flex items-center pointer-events-none -right-1">
                        <svg
                          className="w-10 h-10 text-white"
                          viewBox="0 0 20 20"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            d="M7 7l3-3 3 3m0 6l-3 3-3-3"
                            strokeWidth="0.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    )}
                  </Listbox.Button>
                </span>

                <Transition
                  show={open}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  className="absolute z-50 mt-1 -ml-4 bg-white shadow-lg rounded-2xl"
                >
                  <Listbox.Options
                    static
                    className="overflow-auto leading-6 border divide-y shadow-lg max-h-60 rounded-2xl w-segments-265px focus:outline-none sm:text-sm sm:leading-5 border-segments-gray "
                  >
                    {stores?.map((store) => (
                      <Listbox.Option key={store.id} value={store.id}>
                        {({ selected, active }) => (
                          <div
                            className={`${
                              active
                                ? 'bg-segments-accent-green-3'
                                : 'text-gray-900'
                            } cursor-default select-none relative py-4 pl-12 pr-4 text-base`}
                          >
                            <span
                              className={`${
                                selected ? 'font-semibold' : 'font-normal'
                              } block truncate`}
                            >
                              {store.name ?? store.store_name}
                            </span>
                            {selected && (
                              <span
                                className={`${
                                  active ? 'text-black' : 'text-segments-dark'
                                } absolute inset-y-0 left-0 flex items-center pl-6`}
                              >
                                <CheckedIcon />
                              </span>
                            )}
                          </div>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </>
          )}
        </Listbox>
      </div>
    </div>
  );
}
